import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { ITag } from "services/types";
import { useTagsQuery } from "queries";
import { SearchControl } from "components/controls/SearchControl";
import { useDebounce } from "usehooks-ts";
import { clsx } from "clsx";
import { PinkBaseButton, PRIMARY, SECONDARY } from "components/buttons/PinkBaseButton";
import { Loader } from "components/UI/Loader";
import InfiniteScroll from "react-infinite-scroll-component";
import "./TagSelector.scss";

const SEARCH_DELAY = 500;
const TAGS_PER_PAGE = 100;

interface ITagSelector {
  onChange: (tags: ITag[]) => void;
  defaultSelectedTags?: string[];
  invertColor?: boolean;
}

export const TagSelector: React.FC<ITagSelector> = (props): JSX.Element => {
  const { onChange, defaultSelectedTags, invertColor } = props;
  const { data, isLoading, isSuccess } = useTagsQuery();
  const [tags, setTags] = useState<ITag[]>([]);
  const [selected, setSelected] = useState<string[]>(defaultSelectedTags || []);
  const [pattern, setPattern] = useState<string>("");
  const [visibleCount, setVisibleCount] = useState(TAGS_PER_PAGE);
  const debouncedPattern = useDebounce(pattern, SEARCH_DELAY);
  const scrollableDivRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    if (isSuccess && data) {
      setTags(data.tags);
    }
  }, [isSuccess, data]);

  const matchingTags = useMemo(() => {
    const lowerCasePattern = debouncedPattern.toLowerCase();
    return tags.filter(tag => tag.name.toLowerCase().includes(lowerCasePattern));
  }, [debouncedPattern, tags]);

  const selectedTags = useMemo(() => {
    return tags.filter(tag => selected.includes(tag.name));
  }, [tags, selected]);

  const unselectedTags = useMemo(() => {
    return matchingTags.filter(tag => !selected.includes(tag.name));
  }, [matchingTags, selected]);

  const visibleUnselectedTags = useMemo(() => {
    return unselectedTags.slice(0, visibleCount);
  }, [unselectedTags, visibleCount]);

  useEffect(() => {
    onChange(selectedTags);
  }, [onChange, selectedTags]);

  const onClick = useCallback((tag: ITag) => {
    if (selected.includes(tag.name)) {
      setSelected(prev => prev.filter(name => name !== tag.name));
    } else {
      setSelected(prev => [...prev, tag.name]);
    }
  }, [selected]);

  const onType = (value: string): void => {
    setPattern(value);
    setVisibleCount(TAGS_PER_PAGE);
  };

  const loadMore = () => {
    setVisibleCount(count => count + TAGS_PER_PAGE);
  };

  const renderTag = useCallback((tag: ITag, isSelected: boolean) => (
    <div className={clsx("TagButton", isSelected && "TagButton_isSelected")} key={tag.name}>
      <PinkBaseButton variant={isSelected ? PRIMARY : SECONDARY} onClick={() => onClick(tag)}>
        {tag.name}
      </PinkBaseButton>
    </div>
  ), [onClick]);

  return (
    <div className={clsx("TagSelector", invertColor && "TagSelector_isInverted")}>
      <div className="TagSelector-SearchBar">
        <SearchControl
          onChange={onType}
          value={pattern}
          placeholder="Type to search for tags..."
        />
      </div>
      {isLoading ? <Loader /> : (
        <div className="TagSelector-TagList" ref={scrollableDivRef}>
          {/* {!matchingTags.length ? <div className="TagButton">The tag does not exists.</div> : ( */}
          {/* <> */}
          <InfiniteScroll
            dataLength={visibleUnselectedTags.length}
            next={loadMore}
            hasMore={visibleUnselectedTags.length < unselectedTags.length}
            loader={<Loader />}
            scrollableTarget={scrollableDivRef.current as React.ReactNode}
          >
            {selectedTags.map((tag) => renderTag(tag, true))}
            {matchingTags.length ?
              visibleUnselectedTags.map(tag => renderTag(tag, false)) :
              <div className="TagButton TagButton_isNoTags">The tag does not exists.</div>
            }
          </InfiniteScroll>
          {/* </> */}
          {/* )} */}
        </div>
      )}
    </div>
  );
};
